import browserLocale from 'browser-locale';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

import 'dayjs/locale/en';

export async function initializeDayjs() {
  dayjs.extend(advancedFormat);
  dayjs.extend(utc);
  dayjs.extend(relativeTime);
  dayjs.extend(localizedFormat);
  dayjs.extend(localeData);
  dayjs.extend(calendar);
  dayjs.extend(timezone);
  dayjs.extend(duration);
  dayjs.extend(isBetween);
  dayjs.extend(isoWeek);
  dayjs.extend(quarterOfYear);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(minMax);
  dayjs.extend(weekday);

  const locale = browserLocale();

  switch (locale) {
    case 'de': // Generic German
    case 'de-DE': // Germany
    case 'de-AT': // Austria
    case 'de-CH': // Switzerland
    case 'de-LI': // Liechtenstein
    case 'de-LU': // Luxembourg
      await import('dayjs/locale/de');
      dayjs.locale('de');
      break;

    case 'fr': // Generic French
    case 'fr-FR': // France
      await import('dayjs/locale/fr');
      dayjs.locale('fr');
      break;

    case 'sv': // Generic Swedish
    case 'sv-FI': // Finland
    case 'sv-SE': // Sweden
      await import('dayjs/locale/sv');
      dayjs.locale('sv');
      break;

    case 'es-ES': // Spain Spanish
    case 'es': // Map generic Spanish to Spain Spanish
    case 'es-419': // Generic Latin America Spanish
    case 'es-AR': // Argentina
    case 'es-CL': // Chile
    case 'es-CR': // Costa Rica
    case 'es-HN': // Honduras
    case 'es-MX': // Mexico
    case 'es-PE': // Peru
    case 'es-UY': // Uruguay
    case 'es-VE': // Venezuela
    case 'es-CO': // Colombia
      await import('dayjs/locale/es');
      dayjs.locale('es');
      break;

    case 'pt-BR': // Brazil
    case 'pt': // Map generic Portuguese to Portugal
    case 'pt-PT': // Portugal
      await import('dayjs/locale/pt');
      dayjs.locale('pt');
      break;

    case 'it': // Generic Italian
    case 'it-IT': // Italy Italian
    case 'it-CH': // Switzerland Italian
      await import('dayjs/locale/it');
      dayjs.locale('it');
      break;

    case 'nl': // Generic Dutch
    case 'nl-NL': // Netherlands
      await import('dayjs/locale/nl');
      dayjs.locale('nl');
      break;

    case 'zh': // Generic Chinese
    case 'zh-CN': // Simplified Chinese
    case 'zh-HK': // Chinese - Hong Kong
    case 'zh-TW': // Chinese - Taiwan
      await import('dayjs/locale/zh-cn');
      dayjs.locale('zh-cn');
      break;

    case 'en': // Generic English
    case 'en-US': // USA
    case 'en-GB': // Britain
    case 'en-AU': // Australia
    default:
      dayjs.locale('en');
      break;
  }
}

export const getTimezoneAbbreviation = () => {
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return dayjs().tz(currentTimezone).format('z');
};
